const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/;

const pad = (value) => String(value).padStart(2, '0');

export const resolveLanguage = (language = 'pt-BR') => {
  if (language?.startsWith('en')) return 'en';
  if (language?.startsWith('es')) return 'es';
  return 'pt-BR';
};

export const resolveLocale = (language = 'pt-BR') => {
  const resolved = resolveLanguage(language);
  if (resolved === 'en') return 'en-US';
  if (resolved === 'es') return 'es-ES';
  return 'pt-BR';
};

export const parseCivilDate = (value) => {
  if (!value) return null;
  const civilMatch = String(value).match(DATE_ONLY_PATTERN);
  if (civilMatch) {
    return new Date(Number(civilMatch[1]), Number(civilMatch[2]) - 1, Number(civilMatch[3]), 12, 0, 0);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const toDateInputValue = (value) => {
  if (!value) return '';
  const civilMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (civilMatch) return `${civilMatch[1]}-${civilMatch[2]}-${civilMatch[3]}`;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const formatCivilDate = (value, locale = 'pt-BR', options) => {
  const date = parseCivilDate(value);
  return date ? date.toLocaleDateString(locale, options) : '';
};

export const todayInputValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const formatDateInput = (value, language = 'pt-BR') => {
  const normalized = toDateInputValue(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const [, year, month, day] = match;
  return resolveLanguage(language) === 'en'
    ? `${month}/${day}/${year}`
    : `${day}/${month}/${year}`;
};

export const parseDateInput = (value, language = 'pt-BR') => {
  const match = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return '';
  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]);
  const month = resolveLanguage(language) === 'en' ? first : second;
  const day = resolveLanguage(language) === 'en' ? second : first;
  const date = new Date(year, month - 1, day, 12, 0, 0);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) return '';
  return `${year}-${pad(month)}-${pad(day)}`;
};

export const maskDateInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export const datePlaceholder = (language = 'pt-BR') => (
  resolveLanguage(language) === 'en' ? 'MM/DD/YYYY' : 'DD/MM/AAAA'
);

export const formatLocalizedDate = (value, language = 'pt-BR', options) => {
  if (!options) return formatDateInput(value, language);
  return formatCivilDate(value, resolveLocale(language), options);
};

export const formatLocalizedDateTime = (value, language = 'pt-BR') => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(resolveLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const currentTimeValue = () => {
  const now = new Date();
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
};
